import type { ContactOption } from '@/entities/contacts';
import type { Organization } from '@/entities/organizations';

export type SrfPartyRole = 'sender' | 'receiver' | 'clearingAgent';

export interface SrfPartyMatch {
  id: string;
  name: string;
}

export interface SrfResolvedParty {
  organization?: SrfPartyMatch;
  contact?: SrfPartyMatch;
}

export type SrfResolvedParties = Partial<Record<SrfPartyRole, SrfResolvedParty>>;

export const SRF_PARTY_FIELDS = {
  sender: { organization: 'senderOrganization', contact: 'senderContact' },
  receiver: { organization: 'receiverOrganization', contact: 'receiverContact' },
  clearingAgent: { organization: 'clearingAgent', contact: 'clearingAgentContact' }
} as const;

const normalized = (value?: string | null) => value?.trim().toLowerCase();

/** The backend's `equalsIgnoreCase`: case and surrounding spaces do not count, two missing values match, one does not. */
const sameText = (a?: string | null, b?: string | null) =>
  a == null || b == null ? a == b : normalized(a) === normalized(b);

/** The name lookup of the backend's `resolveOrg`: the whole name, ignoring case and surrounding spaces. */
export const findOrganizationByName = (organizations: Organization[] | undefined, name?: string | null) => {
  const wanted = normalized(name);
  return wanted ? organizations?.find((organization) => normalized(organization.name) === wanted) : undefined;
};

/**
 * The lookup of the backend's `getOrCreateContact`: same name and same first email. The contacts
 * endpoint returns the first email as `email`. Where the backend finds nothing it creates the
 * contact; the form cannot, so no match leaves the field to the user.
 */
export const findContact = (contacts: ContactOption[] | undefined, name?: string | null, email?: string | null) =>
  normalized(name)
    ? contacts?.find((contact) => sameText(contact.name, name) && sameText(contact.email, email))
    : undefined;
